import type { CustomerProfileUpdateInput } from '../../types'
import type { CustomerPrivacyDashboard } from '../../customerPrivacyApi'
import type { ShopLocale, ShopMessageKey } from '../../../i18n'
import {
  CustomerAreaLayoutShell,
  type CustomerAreaNavItem,
} from '../customer-area'
import { DeleteAccountCard } from './DeleteAccountCard'
import { PrivacyMetricsSection } from './PrivacyMetricsSection'
import { PrivacyOverviewAndExportSection } from './PrivacyOverviewAndExportSection'
import { PrivacyProfileAndConsentsSection } from './PrivacyProfileAndConsentsSection'
import { PrivacyQuickLinksSection } from './PrivacyQuickLinksSection'
import { PrivacyRecentActivityCard } from './PrivacyRecentActivityCard'
import { PrivacyStatusNotices } from './PrivacyStatusNotices'

type TranslateFn = (key: ShopMessageKey, params?: Record<string, string | number>) => string

type PrivacyPageContentProps = {
  dashboard: CustomerPrivacyDashboard
  navItems: CustomerAreaNavItem[]
  latestRequest: CustomerPrivacyDashboard['requests'][number] | null
  accountStatusLabel: string
  exportStateLabel: string
  readyToDownload: boolean
  privacyUpdatedAtValue: string
  refreshing: boolean
  error: string
  notice: string
  exportRequesting: boolean
  exportDownloading: boolean
  consentsSaving: boolean
  deletionSubmitting: boolean
  locale: ShopLocale
  t: TranslateFn
  toLocalizedPath: (path: string) => string
  onUpdateProfile: (payload: CustomerProfileUpdateInput) => Promise<boolean>
  onRefresh: () => void
  onRequestExport: () => void
  onDownloadExport: () => void
  onSaveConsents: (input: { marketingEmail: boolean; promotionalNotifications: boolean }) => void
  onRequestDeletion: (password: string) => void
  onNavigate: (targetPath: string) => void
}

export const PrivacyPageContent = ({
  dashboard,
  navItems,
  latestRequest,
  accountStatusLabel,
  exportStateLabel,
  readyToDownload,
  privacyUpdatedAtValue,
  refreshing,
  error,
  notice,
  exportRequesting,
  exportDownloading,
  consentsSaving,
  deletionSubmitting,
  locale,
  t,
  toLocalizedPath,
  onUpdateProfile,
  onRefresh,
  onRequestExport,
  onDownloadExport,
  onSaveConsents,
  onRequestDeletion,
  onNavigate,
}: PrivacyPageContentProps) => (
  <CustomerAreaLayoutShell
    eyebrow={t('common.customerArea')}
    title={t('customer.privacy.title')}
    subtitle={t('customer.privacy.subtitle')}
    navItems={navItems}
    activeNavId="privacy"
    onNavigate={onNavigate}
    actions={[
      {
        id: 'refresh',
        label: t('customer.privacy.refresh'),
        onClick: onRefresh,
        variant: 'secondary',
      },
    ]}
  >
    <PrivacyStatusNotices
      error={error}
      notice={notice}
      refreshing={refreshing}
      refreshingLabel={t('customer.privacy.refreshing')}
    />

    <PrivacyMetricsSection
      accountStatusLabel={accountStatusLabel}
      exportStateLabel={exportStateLabel}
      privacyUpdatedAtValue={privacyUpdatedAtValue}
      accountStatusTone={dashboard.account.accountStatus === 'active' ? 'success' : 'warning'}
      t={t}
    />

    <PrivacyQuickLinksSection
      title={t('customer.privacy.quickLinksTitle')}
      subtitle={t('customer.privacy.quickLinksSubtitle')}
      exportTitle={t('customer.privacy.quickAction.exportTitle')}
      exportDescription={t('customer.privacy.quickAction.exportDescription')}
      exportCta={t('customer.privacy.export.request')}
      profileTitle={t('customer.privacy.quickAction.profileTitle')}
      profileDescription={t('customer.privacy.quickAction.profileDescription')}
      profileCta={t('customer.privacy.quickAction.profileCta')}
      preferencesTitle={t('customer.privacy.quickAction.preferencesTitle')}
      preferencesDescription={t('customer.privacy.quickAction.preferencesDescription')}
      preferencesCta={t('customer.privacy.quickAction.preferencesCta')}
      deleteTitle={t('customer.privacy.quickAction.deleteTitle')}
      deleteDescription={t('customer.privacy.quickAction.deleteDescription')}
      deleteCta={t('customer.privacy.quickAction.deleteCta')}
      onRequestExport={onRequestExport}
      onGoProfile={() => onNavigate(toLocalizedPath('/dashboard/profile'))}
      onGoPreferences={() => onNavigate(toLocalizedPath('/dashboard/preferences'))}
      onScrollToDelete={() => {
        const section = document.getElementById('privacy-delete-account')
        section?.scrollIntoView({ behavior: 'smooth', block: 'start' })
      }}
    />

    <PrivacyOverviewAndExportSection
      dashboard={dashboard}
      locale={locale}
      accountStatusLabel={accountStatusLabel}
      exportStateLabel={exportStateLabel}
      privacyUpdatedAtValue={privacyUpdatedAtValue}
      readyToDownload={readyToDownload}
      exportRequesting={exportRequesting}
      exportDownloading={exportDownloading}
      onRequestExport={onRequestExport}
      onDownloadExport={onDownloadExport}
      t={t}
    />

    <PrivacyProfileAndConsentsSection
      dashboard={dashboard}
      locale={locale}
      consentsSaving={consentsSaving}
      onUpdateProfile={onUpdateProfile}
      onSaveConsents={onSaveConsents}
      t={t}
    />

    <PrivacyRecentActivityCard
      title={t('customer.privacy.activity.title')}
      subtitle={t('customer.privacy.activity.description')}
      createdAtLabel={(date) => t('customer.privacy.activity.createdAt', { date })}
      emptyLabel={t('customer.privacy.activity.empty')}
      locale={locale}
      neverLabel={t('common.never')}
      latestRequest={latestRequest}
    />

    <div id="privacy-delete-account">
      <DeleteAccountCard
        title={t('customer.privacy.delete.title')}
        description={t('customer.privacy.delete.description')}
        warningTitle={t('customer.privacy.delete.warningTitle')}
        warningBody={t('customer.privacy.delete.warningBody')}
        openModalLabel={t('customer.privacy.delete.action')}
        modalTitle={t('customer.privacy.delete.modalTitle')}
        modalDescription={t('customer.privacy.delete.modalDescription')}
        passwordLabel={t('customer.privacy.delete.passwordLabel')}
        confirmLabel={t('customer.privacy.delete.confirmLabel')}
        cancelLabel={t('customer.privacy.delete.cancel')}
        submitLabel={t('customer.privacy.delete.submit')}
        submitBusyLabel={t('customer.privacy.delete.submitting')}
        submitting={deletionSubmitting}
        onSubmit={onRequestDeletion}
      />
    </div>
  </CustomerAreaLayoutShell>
)
